import { asDefined, assert, isDefined, panic, UUID } from "@opendaw/lib-std";
import { AudioUnitBoxAdapter } from "./audio-unit/AudioUnitBoxAdapter";
import { DelayDeviceBoxAdapter } from "./devices/audio-effects/DelayDeviceBoxAdapter";
import { ReverbDeviceBoxAdapter } from "./devices/audio-effects/ReverbDeviceBoxAdapter";
import { RevampDeviceBoxAdapter } from "./devices/audio-effects/RevampDeviceBoxAdapter";
import { AudioFileBoxAdapter } from "./audio/AudioFileBoxAdapter";
import { AudioRegionBoxAdapter } from "./timeline/region/AudioRegionBoxAdapter";
import { TimelineBoxAdapter } from "./timeline/TimelineBoxAdapter";
import { MarkerBoxAdapter } from "./timeline/MarkerBoxAdapter";
import { ModularAdapter } from "./modular/modular";
import { ModuleDelayAdapter } from "./modular/modules/delay";
import { ModuleMultiplierAdapter } from "./modular/modules/multiplier";
import { ModuleConnectionAdapter } from "./modular/connection";
import { ModularAudioOutputAdapter } from "./modular/modules/audio-output";
import { ModularAudioInputAdapter } from "./modular/modules/audio-input";
import { ModularDeviceBoxAdapter } from "./devices/audio-effects/ModularDeviceBoxAdapter";
import { DeviceInterfaceKnobAdapter } from "./modular/user-interface";
import { ModuleGainAdapter } from "./modular/modules/gain";
import { AudioBusBoxAdapter } from "./audio-unit/AudioBusBoxAdapter";
import { AuxSendBoxAdapter } from "./audio-unit/AuxSendBoxAdapter";
import { RootBoxAdapter } from "./RootBoxAdapter";
import { NoteEventBoxAdapter } from "./timeline/event/NoteEventBoxAdapter";
import { NoteRegionBoxAdapter } from "./timeline/region/NoteRegionBoxAdapter";
import { NoteEventCollectionBoxAdapter } from "./timeline/collection/NoteEventCollectionBoxAdapter";
import { ValueEventBoxAdapter } from "./timeline/event/ValueEventBoxAdapter";
import { ValueRegionBoxAdapter } from "./timeline/region/ValueRegionBoxAdapter";
import { ValueEventCollectionBoxAdapter } from "./timeline/collection/ValueEventCollectionBoxAdapter";
import { NoteClipBoxAdapter } from "./timeline/clip/NoteClipBoxAdapter";
import { AudioClipBoxAdapter } from "./timeline/clip/AudioClipBoxAdapter";
import { ValueClipBoxAdapter } from "./timeline/clip/ValueClipBoxAdapter";
import { TrackBoxAdapter } from "./timeline/TrackBoxAdapter";
import { TapeDeviceBoxAdapter } from "./devices/instruments/TapeDeviceBoxAdapter";
import { VaporisateurDeviceBoxAdapter } from "./devices/instruments/VaporisateurDeviceBoxAdapter";
import { ArpeggioDeviceBoxAdapter } from "./devices/midi-effects/ArpeggioDeviceBoxAdapter";
import { PitchDeviceBoxAdapter } from "./devices/midi-effects/PitchDeviceBoxAdapter";
import { NanoDeviceBoxAdapter } from "./devices/instruments/NanoDeviceBoxAdapter";
import { PlayfieldDeviceBoxAdapter } from "./devices/instruments/PlayfieldDeviceBoxAdapter";
import { StereoToolDeviceBoxAdapter } from "./devices/audio-effects/StereoToolDeviceBoxAdapter";
import { PlayfieldSampleBoxAdapter } from "./devices/instruments/Playfield/PlayfieldSampleBoxAdapter";
import { ZeitgeistDeviceBoxAdapter } from "./devices/midi-effects/ZeitgeistDeviceBoxAdapter";
import { GrooveShuffleBoxAdapter } from "./grooves/GrooveShuffleBoxAdapter";
import { UnknownAudioEffectDeviceBoxAdapter } from "./devices/audio-effects/UnknownAudioEffectDeviceBoxAdapter";
import { UnknownMidiEffectDeviceBoxAdapter } from "./devices/midi-effects/UnknownMidiEffectDeviceBoxAdapter";
import { SoundfontDeviceBoxAdapter } from "./devices/instruments/SoundfontDeviceBoxAdapter";
import { SoundfontFileBoxAdapter } from "./soundfont/SoundfontFileBoxAdapter";
import { CompressorDeviceBoxAdapter } from "./devices/audio-effects/CompressorDeviceBoxAdapter";
import { CrusherDeviceBoxAdapter } from "./devices/audio-effects/CrusherDeviceBoxAdapter";
import { FoldDeviceBoxAdapter } from "./devices/audio-effects/FoldDeviceBoxAdapter";
import { MIDIOutputDeviceBoxAdapter } from "./devices/instruments/MIDIOutputDeviceBoxAdapter";
import { VelocityDeviceBoxAdapter } from "./devices/midi-effects/VelocityDeviceBoxAdapter";
export class BoxAdapters {
    #context;
    #adapters;
    #deleted;
    #terminable;
    constructor(context) {
        this.#context = context;
        this.#adapters = UUID.newSet(adapter => adapter.uuid);
        this.#deleted = new Set();
        this.#terminable = this.#context.boxGraph.subscribeToAllUpdates({
            onUpdate: (update) => {
                if (update.type === "delete") {
                    const adapter = this.#adapters.getOrNull(update.uuid);
                    if (isDefined(adapter)) {
                        this.#deleted.add(adapter.box);
                        this.#adapters.removeByValue(adapter).terminate();
                    }
                }
            }
        });
    }
    terminate() {
        this.#adapters.values().forEach(adapter => adapter.terminate());
        this.#adapters.clear();
        this.#terminable.terminate();
    }
    adapterFor(box, checkType) {
        if (this.#deleted.has(box)) {
            return panic(`Cannot resolve adapter for already deleted box: ${box}`);
        }
        let adapter = this.#adapters.getOrNull(box.address.uuid);
        if (adapter === null) {
            adapter = this.#create(box);
            const added = this.#adapters.add(adapter);
            assert(added, `Could not add adapter for ${box}`);
        }
        if (typeof checkType === "function") {
            return Object.hasOwn(checkType, "prototype")
                ? adapter instanceof checkType ? adapter : panic(`${adapter} is not instance of ${checkType}`)
                : checkType(adapter) ? adapter : panic(`${adapter} did not pass custom type guard`);
        }
        return panic("Unknown checkType method");
    }
    #create(unknownBox) {
        return asDefined(unknownBox.accept({
            visitRootBox: (box) => new RootBoxAdapter(this.#context, box),
            visitArpeggioDeviceBox: (box) => new ArpeggioDeviceBoxAdapter(this.#context, box),
            visitPitchDeviceBox: (box) => new PitchDeviceBoxAdapter(this.#context, box),
            visitVelocityDeviceBox: (box) => new VelocityDeviceBoxAdapter(this.#context, box),
            visitStereoToolDeviceBox: (box) => new StereoToolDeviceBoxAdapter(this.#context, box),
            visitCrusherDeviceBox: (box) => new CrusherDeviceBoxAdapter(this.#context, box),
            visitFoldDeviceBox: (box) => new FoldDeviceBoxAdapter(this.#context, box),
            visitDelayDeviceBox: (box) => new DelayDeviceBoxAdapter(this.#context, box),
            visitCompressorDeviceBox: (box) => new CompressorDeviceBoxAdapter(this.#context, box),
            visitReverbDeviceBox: (box) => new ReverbDeviceBoxAdapter(this.#context, box),
            visitRevampDeviceBox: (box) => new RevampDeviceBoxAdapter(this.#context, box),
            visitUnknownAudioEffectDeviceBox: (box) => new UnknownAudioEffectDeviceBoxAdapter(this.#context, box),
            visitPlayfieldDeviceBox: (box) => new PlayfieldDeviceBoxAdapter(this.#context, box),
            visitPlayfieldSampleBox: (box) => new PlayfieldSampleBoxAdapter(this.#context, box),
            visitModularDeviceBox: (box) => new ModularDeviceBoxAdapter(this.#context, box),
            visitTapeDeviceBox: (box) => new TapeDeviceBoxAdapter(this.#context, box),
            visitNanoDeviceBox: (box) => new NanoDeviceBoxAdapter(this.#context, box),
            visitVaporisateurDeviceBox: (box) => new VaporisateurDeviceBoxAdapter(this.#context, box),
            visitMIDIOutputDeviceBox: (box) => new MIDIOutputDeviceBoxAdapter(this.#context, box),
            visitSoundfontDeviceBox: (box) => new SoundfontDeviceBoxAdapter(this.#context, box),
            visitModularBox: (box) => new ModularAdapter(this.#context, box),
            visitModuleConnectionBox: (box) => new ModuleConnectionAdapter(this.#context, box),
            visitModuleDelayBox: (box) => new ModuleDelayAdapter(this.#context, box),
            visitModuleGainBox: (box) => new ModuleGainAdapter(this.#context, box),
            visitModuleMultiplierBox: (box) => new ModuleMultiplierAdapter(this.#context, box),
            visitModularAudioInputBox: (box) => new ModularAudioInputAdapter(this.#context, box),
            visitModularAudioOutputBox: (box) => new ModularAudioOutputAdapter(this.#context, box),
            visitDeviceInterfaceKnobBox: (box) => new DeviceInterfaceKnobAdapter(this.#context, box),
            visitAudioUnitBox: (box) => new AudioUnitBoxAdapter(this.#context, box),
            visitAudioBusBox: (box) => new AudioBusBoxAdapter(this.#context, box),
            visitAuxSendBox: (box) => new AuxSendBoxAdapter(this.#context, box),
            visitSoundfontFileBox: (box) => new SoundfontFileBoxAdapter(this.#context, box),
            visitAudioFileBox: (box) => new AudioFileBoxAdapter(this.#context, box),
            visitAudioClipBox: (box) => new AudioClipBoxAdapter(this.#context, box),
            visitAudioRegionBox: (box) => new AudioRegionBoxAdapter(this.#context, box),
            visitNoteEventBox: (box) => new NoteEventBoxAdapter(this.#context, box),
            visitNoteEventCollectionBox: (box) => new NoteEventCollectionBoxAdapter(this.#context, box),
            visitNoteClipBox: (box) => new NoteClipBoxAdapter(this.#context, box),
            visitNoteRegionBox: (box) => new NoteRegionBoxAdapter(this.#context, box),
            visitValueEventBox: (box) => new ValueEventBoxAdapter(this.#context, box),
            visitValueEventCollectionBox: (box) => new ValueEventCollectionBoxAdapter(this.#context, box),
            visitValueClipBox: (box) => new ValueClipBoxAdapter(this.#context, box),
            visitValueRegionBox: (box) => new ValueRegionBoxAdapter(this.#context, box),
            visitTrackBox: (box) => new TrackBoxAdapter(this.#context, box),
            visitTimelineBox: (box) => new TimelineBoxAdapter(this.#context, box),
            visitMarkerBox: (box) => new MarkerBoxAdapter(this.#context, box),
            visitZeitgeistDeviceBox: (box) => new ZeitgeistDeviceBoxAdapter(this.#context, box),
            visitUnknownMidiEffectDeviceBox: (box) => new UnknownMidiEffectDeviceBoxAdapter(this.#context, box),
            visitGrooveShuffleBox: (box) => new GrooveShuffleBoxAdapter(this.#context, box)
        }), `Could not find factory for ${unknownBox}`);
    }
}
